// Prepares a question-bank explanation for a Markdown renderer that never
// outputs raw HTML (the Daily ABSITE page and the quiz review). Without this,
// clinical text can lose meaning:
// - A line or list item that starts with a threshold (">5 cm", "> 50%",
//   ">=3") becomes a blockquote and loses its ">". The ">" is escaped.
// - "<" before a word ("Ca <LLN and PTH >ULN") reads as an HTML tag. Only tags
//   with real HTML element names are removed, keeping their text; any other
//   "<" before a letter is escaped so it shows. Markdown autolinks
//   (<https://…>, <name@host>) are left alone.
// - Removing tags must not fuse text or create structure: "10<sup>9</sup>/L"
//   becomes "10^9/L", block and list tags become line breaks (escaping a
//   ">"/"-"/"#"… that would start the new line), table cells stay apart, and a
//   <br> inside a Markdown table row becomes "; " so the row survives.
const LINE_BREAK_TAG = /<br\s*\/?>/gi;
// Inline tags (e.g. <b>) may sit inside a superscript, and so may a "<" that
// starts no tag ("p<0.05"); a nested <sup> may not.
const SUPERSCRIPT = /<sup\b[^<>]*>((?:[^<]|<(?![A-Za-z/])|<\/?(?!sup\b)[A-Za-z][^<>]*>)*)<\/sup\s*>/gi;
const INNER_TAG = /<\/?[A-Za-z][^<>]*>/g;
const BLOCK_TAG_NAMES = "p|div|h[1-6]|blockquote|pre|table|thead|tbody|tfoot|tr|ul|ol|section|article|header|footer";
const BLOCK_TAG = new RegExp(`<\\/?(?:${BLOCK_TAG_NAMES})\\b[^<>]*>|<hr\\b[^<>]*>`, "gi");
const LIST_ITEM_TAG = /<\/?li\b[^<>]*>/gi;
const CELL_TAG = /<\/?t[dh]\b[^<>]*>/gi;
// Tags that start a new line or paragraph, with any spaces after them (which
// would otherwise indent the new line into a code block). A leading number
// that would start an ordered list ("1998. The trial") is captured to escape.
const BREAKING_TAG = new RegExp(`(<\\/?(?:${BLOCK_TAG_NAMES})\\b[^<>]*>|<hr\\b[^<>]*>|<\\/?li\\b[^<>]*>|<br\\s*\\/?>\\n?)[ \\t]*(\\d+(?=[.)](?:[ \\t\\n]|$)))?`, "gi");
// What would make the start of that new line Markdown structure instead of text.
// A rule or underline counts when only spaces or a tag (soon a break) follow it.
const STARTS_BLOCK = /(?:>|[-+*](?:[ \t\n]|$)|(?:-{3,}|\*{3,}|_{3,}|=+)[ \t]*(?:[\n<]|$)|#{1,6}(?:[ \t\n#]|$))/y;
// CommonMark's open and closing tag grammar, so a tag ends where Markdown says.
const TAG = /<(\/?)([A-Za-z][A-Za-z0-9-]*)((?:\s+[A-Za-z_:][\w.:-]*(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*\s*\/?)>/g;
const HTML_ELEMENTS = new Set([
  "a", "abbr", "article", "aside", "audio", "b", "big", "blockquote", "body", "br", "button", "canvas",
  "caption", "center", "cite", "code", "col", "colgroup", "dd", "del", "details", "dfn", "div", "dl",
  "dt", "em", "embed", "figcaption", "figure", "font", "footer", "form", "h1", "h2", "h3", "h4", "h5",
  "h6", "head", "header", "hr", "html", "i", "iframe", "img", "input", "ins", "kbd", "label", "li",
  "link", "main", "mark", "meta", "nav", "noscript", "object", "ol", "option", "p", "picture", "pre",
  "q", "s", "samp", "script", "section", "select", "small", "source", "span", "strike", "strong",
  "style", "sub", "summary", "sup", "svg", "table", "tbody", "td", "template", "textarea", "tfoot",
  "th", "thead", "title", "tr", "tt", "u", "ul", "var", "video",
]);
// Bounded: removing one tag can join its neighbours into another ("<scr<b>ipt>").
const MAX_TAG_PASSES = 5;
const AUTOLINK = /^<(?:[A-Za-z][A-Za-z0-9+.-]{1,31}:[^\s<>]*|[\w.!#$%&'*+/=?^`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?)>/;
const TAG_OPEN = /(\\?)<(?=[A-Za-z/])/g;
const LEADING_THRESHOLD = /^([ \t]*(?:(?:[-*+]|\d+[.)])[ \t]*)?)>(?=[ \t]*(?:\d|=|\.\d|\/=))/gm;

const removeHtmlElementTags = (text) => {
  let current = text;
  for (let pass = 0; pass < MAX_TAG_PASSES; pass += 1) {
    const next = current.replace(TAG, (tag, _slash, name) => (HTML_ELEMENTS.has(name.toLowerCase()) ? "" : tag));
    if (next === current) break;
    current = next;
  }
  return current;
};

const escapeTagOpen = (match, backslash, offset, text) => {
  if (backslash || AUTOLINK.test(text.slice(offset))) return match;
  return String.raw`\<`;
};

const toSuperscript = (_tag, inner) => {
  const value = inner.replace(INNER_TAG, "").trim();
  if (!value) return "";
  return /\s/.test(value) ? `^(${value})` : `^${value}`;
};

// A Markdown table row ends at the line end, so inside one a break or block
// tag must stay on the line.
const separateWithinTableRow = (line) => (line.includes("|")
  ? line.replace(LINE_BREAK_TAG, "; ").replace(LIST_ITEM_TAG, (tag) => (tag[1] === "/" ? "; " : ""))
    .replace(BLOCK_TAG, " ").replace(CELL_TAG, " ")
  : line);

const startsBlockAt = (text, index) => {
  STARTS_BLOCK.lastIndex = index;
  return STARTS_BLOCK.test(text);
};

// Line and list-item breaks become hard line breaks, other block tags a blank
// line. The text that follows is escaped if it would otherwise become a quote,
// list, heading or rule ("<li>>ULN</li>" keeps its ">").
const breakBefore = (match, tag, digits, offset, text) => {
  const separator = /^<(?:br|\/?li)\b/i.test(tag) ? "  \n" : "\n\n";
  if (digits) return `${separator}${digits}\\`;
  return startsBlockAt(text, offset + match.length) ? `${separator}\\` : separator;
};

const separateHtmlBlocks = (text) => text
  .split("\n")
  .map(separateWithinTableRow)
  .join("\n")
  .replace(CELL_TAG, " ")
  .replace(BREAKING_TAG, breakBefore);

export const toExplanationMarkdown = (text) => {
  if (typeof text !== "string") return text;
  return removeHtmlElementTags(separateHtmlBlocks(text.replace(SUPERSCRIPT, toSuperscript)))
    .replace(TAG_OPEN, escapeTagOpen)
    .replace(LEADING_THRESHOLD, String.raw`$1\>`);
};
